import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de Celebraciones.
 *
 * ## Los quince endpoints NO cambian
 *
 * Son los mismos que pedía `page-celebrations.js`, con los mismos parámetros.
 *
 * ## Lo que sí cambia: el buscador de personas deja de preguntar por cada tecla
 *
 * `page-celebrations.js` tenía **tres** buscadores de persona, copiados con tres
 * ids distintos. Dos amortiguaban bien y el tercero no amortiguaba nada:
 *
 *     Utils.debounce(async () => { … }, 300)();     // ← `_nhSearch`
 *
 * Construía un debouncer NUEVO en cada pulsación y lo invocaba en el acto, así
 * que su `clearTimeout` limpiaba un temporizador recién nacido y vacío: cada
 * tecla programaba la suya y todas disparaban. Medido en el navegador el
 * 2026-09-11, tecleando «maria» a 60 ms por tecla: **4 peticiones** donde un
 * debounce bien montado hace **1**.
 *
 * Aquí no hay debounce que montar. La búsqueda vive en la CLAVE de la consulta,
 * así que una búsqueda ya hecha no vuelve a preguntar, y los tres buscadores son
 * el mismo componente.
 *
 * ## Los tipos están escritos a mano
 *
 * La misma deuda declarada de siempre: el backend declara un solo
 * `response_model` en 27 routers, así que `/openapi.json` no describe ninguna
 * respuesta. Copiados leyendo `app/routers/celebrations.py`.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

export interface Celebracion {
  user_id: number;
  full_name: string;
  position?: string | null;
  avatar_url?: string | null;
  type?: 'birthday' | 'work_anniversary';
  years?: number;
  days_until: number;
  birth_date_display?: string | null;
}

export interface MesDeCelebraciones {
  birthdays?: Celebracion[];
  anniversaries?: Celebracion[];
}

export interface TipoDeHito {
  kind: string;
  etiqueta: string;
}

export interface HitoRegistrado {
  id: number;
  colaborador: string;
  etiqueta: string;
  fecha: string;
  aviso_personal?: boolean;
  aviso_equipo?: boolean;
}

export interface PersonaDelDirectorio {
  id: number;
  full_name: string;
  position?: string | null;
}

export type EstadoDeIngreso = 'draft' | 'scheduled' | 'published';

export interface TarjetaDeIngreso {
  id: number;
  full_name: string;
  position?: string | null;
  area?: string | null;
  hire_date?: string | null;
  status: EstadoDeIngreso;
  scheduled_at?: string | null;
  card_url?: string | null;
}

/** Lo que el maestro de colaboradores ya sabe de alguien, para precargar la
 *  tarjeta de Nuevo Ingreso en vez de reescribirlo a mano. */
export interface FichaDeColaborador {
  position?: string | null;
  area?: string | null;
  hire_date?: string | null;
  sede?: string | null;
  email?: string | null;
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  proximas: ['celebraciones', 'proximas'] as const,
  mes: (mes: number) => ['celebraciones', 'mes', mes] as const,
  tiposDeHito: ['celebraciones', 'tipos'] as const,
  hitos: ['celebraciones', 'hitos'] as const,
  ingresos: ['celebraciones', 'ingresos'] as const,
  fichaDe: (id: number) => ['celebraciones', 'ficha', id] as const,
};

/* ── Lecturas ──────────────────────────────────────────────────────────── */

export function useProximas() {
  return useQuery({
    queryKey: claves.proximas,
    queryFn: () => api.get<{ items?: Celebracion[] }>('/celebrations/upcoming', { days: 60 }),
  });
}

export function useMes(mes: number) {
  return useQuery({
    queryKey: claves.mes(mes),
    queryFn: () => api.get<MesDeCelebraciones>(`/celebrations/month/${mes}`),
  });
}

/** Los tipos de hito que se pueden registrar a mano.
 *
 *  `habilitada` porque el formulario vive en un diálogo montado siempre, y sin
 *  el interruptor pediría su catálogo al cargar la pantalla. Es el mismo defecto
 *  que se midió contando peticiones al migrar Reconocimientos. */
export function useTiposDeHito(habilitada = true) {
  return useQuery({
    queryKey: claves.tiposDeHito,
    queryFn: () => api.get<TipoDeHito[]>('/celebrations/manual/tipos'),
    enabled: habilitada,
    staleTime: 30 * 60_000,
  });
}

export function useHitos(habilitada = true) {
  return useQuery({
    queryKey: claves.hitos,
    queryFn: () => api.get<HitoRegistrado[]>('/celebrations/manual', { limit: 100 }),
    enabled: habilitada,
  });
}

export function useIngresos(habilitada = true) {
  return useQuery({
    queryKey: claves.ingresos,
    queryFn: () => api.get<{ items: TarjetaDeIngreso[] }>('/celebrations/newhire'),
    enabled: habilitada,
  });
}


/* ── Escrituras ────────────────────────────────────────────────────────── */

/** Lo que cambia cuando se publica una celebración: las tres vistas que la
 *  muestran. **No** el catálogo de motivos, que es estático.
 *
 *  Invalidar `['celebraciones']` entero era lo cómodo y costaba una petición de
 *  más: `/celebrations/manual/tipos` se volvía a pedir cada vez que se
 *  registraba un hito, con el diálogo abierto delante. Se vio contando
 *  peticiones en el navegador, no leyendo el código. */
function refrescarLoPublicado(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: claves.proximas });
  qc.invalidateQueries({ queryKey: ['celebraciones', 'mes'] });
  qc.invalidateQueries({ queryKey: claves.hitos });
  qc.invalidateQueries({ queryKey: claves.ingresos });
}

export function useFelicitar() {
  return useMutation({
    mutationFn: (userId: number) => api.post<{ message?: string }>(`/celebrations/wish/${userId}`, null),
  });
}

export interface HitoNuevo {
  user_id: number;
  kind: string;
  fecha: string | null;
  detalle: string;
  message: string;
  enviar_al_colaborador: boolean;
  publicar_feed: boolean;
  avisar_equipo: boolean;
}

export function useRegistrarHito() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nuevo: HitoNuevo) =>
      api.post<{ message: string; companeros_avisados: number }>('/celebrations/manual', nuevo),
    onSuccess: () => refrescarLoPublicado(qc),
  });
}

export function useBorrarHito() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/celebrations/manual/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.hitos }),
  });
}

export interface TarjetaPedida {
  kind: string;
  user_id: number;
  anios: number | null;
  message: string;
  publish: boolean;
  email_to_admin: boolean;
}

export interface TarjetaGenerada {
  card_url?: string | null;
  post_id?: number | null;
  emailed_to?: string | null;
}

export function useGenerarTarjeta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pedido: TarjetaPedida) => api.post<TarjetaGenerada>('/celebrations/card', pedido),
    // Solo invalida cuando de verdad publicó: una vista previa no cambia nada,
    // y refrescar la pantalla entera por mirar una imagen sería gratuito.
    onSuccess: (_r, pedido) => {
      if (pedido.publish) refrescarLoPublicado(qc);
    },
  });
}

export interface IngresoNuevo {
  full_name: string;
  position: string;
  area: string;
  hire_date: string | null;
  sede: string;
  leader: string;
  email: string | null;
  message: string | null;
  phrase: string | null;
  photo_key: string | null;
  user_id: number | null;
  action: 'draft' | 'schedule' | 'publish';
  publish_feed: boolean;
  send_email: boolean;
  scheduled_at: string | null;
}

export function useGuardarIngreso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nuevo: IngresoNuevo) => api.post<{ message?: string }>('/celebrations/newhire', nuevo),
    onSuccess: () => refrescarLoPublicado(qc),
  });
}

export function usePublicarIngreso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<unknown>(`/celebrations/newhire/${id}/publish`, {}),
    onSuccess: () => refrescarLoPublicado(qc),
  });
}

export function useBorrarIngreso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<unknown>(`/celebrations/newhire/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.ingresos }),
  });
}

export function useSubirFoto() {
  return useMutation({
    mutationFn: (archivo: File) => {
      const datos = new FormData();
      datos.append('file', archivo);
      return api.upload<{ photo_key: string }>('/celebrations/newhire/photo', datos);
    },
  });
}

/** La ficha del maestro, para precargar y BLOQUEAR los campos al vincular.
 *
 *  Se pide cuando hay un id, no antes: es lo que hacía el vanilla y es lo
 *  correcto — sin persona vinculada no hay nada que precargar. */
export function useFichaDe(userId: number | null) {
  return useQuery({
    queryKey: claves.fichaDe(userId ?? 0),
    queryFn: () => api.get<FichaDeColaborador>(`/celebrations/newhire/user/${userId}`),
    enabled: userId != null,
  });
}
